// features/admin/adminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// ── Agences ───────────────────────────────────────────────────────────────────

export const fetchTenants = createAsyncThunk('admin/fetchTenants', async (_, { rejectWithValue }) => {
  try { const res = await api.get('/tenants'); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors du chargement'); }
});

export const createTenant = createAsyncThunk('admin/createTenant', async (dto, { rejectWithValue }) => {
  try { const res = await api.post('/tenants', dto); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors de la création'); }
});

export const updateTenant = createAsyncThunk('admin/updateTenant', async ({ id, dto }, { rejectWithValue }) => {
  try { const res = await api.put(`/tenants/${id}`, dto); return { id, tenant: res.data.tenant }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors de la mise à jour'); }
});

export const deleteTenant = createAsyncThunk('admin/deleteTenant', async (id, { rejectWithValue }) => {
  try { const res = await api.delete(`/tenants/${id}`); return { id, message: res.data.message }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors de la suppression'); }
});

export const blockTenant = createAsyncThunk('admin/blockTenant', async (id, { rejectWithValue }) => {
  try { const res = await api.put(`/tenants/${id}/block`); return { id, message: res.data.message }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors du blocage'); }
});

export const unblockTenant = createAsyncThunk('admin/unblockTenant', async (id, { rejectWithValue }) => {
  try { const res = await api.put(`/tenants/${id}/unblock`); return { id, message: res.data.message }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors du déblocage'); }
});

// ── Utilisateurs clients ──────────────────────────────────────────────────────

export const createUserForTenant = createAsyncThunk(
  'admin/createUserForTenant',
  async ({ tenantId, data }, { rejectWithValue }) => {
    try { const res = await api.post(`/tenants/${tenantId}/users`, data); return res.data; }
    catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors de la création'); }
  }
);

export const fetchAllUsers = createAsyncThunk('admin/fetchAllUsers', async (_, { rejectWithValue }) => {
  try { const res = await api.get('/tenants/users'); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur chargement utilisateurs'); }
});

export const blockUser = createAsyncThunk('admin/blockUser', async (userId, { rejectWithValue }) => {
  try { const res = await api.put(`/tenants/users/${userId}/block`); return { userId, message: res.data.message }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors du blocage'); }
});

export const unblockUser = createAsyncThunk('admin/unblockUser', async (userId, { rejectWithValue }) => {
  try { const res = await api.put(`/tenants/users/${userId}/unblock`); return { userId, message: res.data.message }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors du déblocage'); }
});

export const renewUser = createAsyncThunk('admin/renewUser', async ({ userId, duree }, { rejectWithValue }) => {
  try {
    const res = await api.put(`/tenants/users/${userId}/renew`, { duree });
    return { userId, dateFin: res.data.dateFin, duree: res.data.duree };
  } catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors du renouvellement'); }
});

export const deleteUser = createAsyncThunk('admin/deleteUser', async (userId, { rejectWithValue }) => {
  try { const res = await api.delete(`/tenants/users/${userId}`); return { userId, message: res.data.message }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors de la suppression'); }
});

// ── Comptes SuperAdmin ────────────────────────────────────────────────────────

export const fetchSuperAdmins = createAsyncThunk('admin/fetchSuperAdmins', async (_, { rejectWithValue }) => {
  try { const res = await api.get('/tenants/superadmins'); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur chargement SuperAdmins'); }
});

export const createSuperAdmin = createAsyncThunk('admin/createSuperAdmin', async (dto, { rejectWithValue }) => {
  try { const res = await api.post('/tenants/superadmins', dto); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur création SuperAdmin'); }
});

export const deleteSuperAdmin = createAsyncThunk('admin/deleteSuperAdmin', async (id, { rejectWithValue }) => {
  try { const res = await api.delete(`/tenants/superadmins/${id}`); return { id, message: res.data.message }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur suppression SuperAdmin'); }
});

// ── Comptes Agent ─────────────────────────────────────────────────────────────

export const fetchAgents = createAsyncThunk('admin/fetchAgents', async (_, { rejectWithValue }) => {
  try { const res = await api.get('/tenants/agents'); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur chargement Agents'); }
});

export const createAgent = createAsyncThunk('admin/createAgent', async (dto, { rejectWithValue }) => {
  try { const res = await api.post('/tenants/agents', dto); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur création Agent'); }
});

export const blockAgent = createAsyncThunk('admin/blockAgent', async (id, { rejectWithValue }) => {
  try { const res = await api.put(`/tenants/agents/${id}/block`); return { id, message: res.data.message }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur blocage Agent'); }
});

export const unblockAgent = createAsyncThunk('admin/unblockAgent', async (id, { rejectWithValue }) => {
  try { const res = await api.put(`/tenants/agents/${id}/unblock`); return { id, message: res.data.message }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur déblocage Agent'); }
});

export const deleteAgent = createAsyncThunk('admin/deleteAgent', async (id, { rejectWithValue }) => {
  try { const res = await api.delete(`/tenants/agents/${id}`); return { id, message: res.data.message }; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur suppression Agent'); }
});

// ── Journal d'audit des Agents ────────────────────────────────────────────────

export const fetchAuditLogs = createAsyncThunk('admin/fetchAuditLogs', async (filters = {}, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    if (filters.adminUsername) params.append('adminUsername', filters.adminUsername);
    if (filters.actionType)    params.append('actionType', filters.actionType);
    if (filters.from)          params.append('from', filters.from);
    if (filters.to)            params.append('to', filters.to);
    const res = await api.get(`/tenants/audit-logs?${params.toString()}`);
    return res.data;
  } catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur chargement logs'); }
});

export const changeTenantPack = createAsyncThunk(
  'admin/changeTenantPack',
  async ({ id, pack }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/tenants/${id}/pack`, { pack });
      return { id, pack, ...res.data };
    } catch (err) { return rejectWithValue(err.response?.data?.message || 'Erreur lors du changement de pack'); }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    tenants:     [],
    users:       [],
    superAdmins: [],
    agents:      [],
    auditLogs:   [],
    loading:     false,
    error:       null,
    success:     false,
    successMode: null,
    actionMessage: null,
  },
  reducers: {
    resetSuccess:       (state) => { state.success = false; state.successMode = null; },
    clearError:         (state) => { state.error = null; },
    clearActionMessage: (state) => { state.actionMessage = null; },
  },
  extraReducers: (builder) => {

    builder
      .addCase(fetchTenants.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTenants.fulfilled, (state, action) => { state.loading = false; state.tenants = action.payload; })
      .addCase(fetchTenants.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(createTenant.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(createTenant.fulfilled, (state) => { state.loading = false; state.success = true; state.successMode = 'tenant'; })
      .addCase(createTenant.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(updateTenant.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(updateTenant.fulfilled, (state, action) => {
        state.loading = false; state.success = true; state.successMode = 'tenantUpdate';
        const idx = state.tenants.findIndex(t => t.id === action.payload.id);
        if (idx !== -1 && action.payload.tenant) state.tenants[idx] = action.payload.tenant;
      })
      .addCase(updateTenant.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(deleteTenant.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(deleteTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.tenants = state.tenants.filter(t => t.id !== action.payload.id);
        state.actionMessage = { type: 'success', text: action.payload.message };
      })
      .addCase(deleteTenant.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(blockTenant.pending,   (state) => { state.loading = true; })
      .addCase(blockTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.actionMessage = { type: 'success', text: action.payload.message };
        const t = state.tenants.find(t => t.id === action.payload.id);
        if (t) t.estActif = false;
      })
      .addCase(blockTenant.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(unblockTenant.pending,   (state) => { state.loading = true; })
      .addCase(unblockTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.actionMessage = { type: 'success', text: action.payload.message };
        const t = state.tenants.find(t => t.id === action.payload.id);
        if (t) t.estActif = true;
      })
      .addCase(unblockTenant.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(createUserForTenant.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(createUserForTenant.fulfilled, (state) => { state.loading = false; state.success = true; state.successMode = 'user'; })
      .addCase(createUserForTenant.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(fetchAllUsers.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAllUsers.fulfilled, (state, action) => { state.loading = false; state.users = action.payload; })
      .addCase(fetchAllUsers.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(blockUser.pending,   (state) => { state.loading = true; })
      .addCase(blockUser.fulfilled, (state, action) => {
        state.loading = false;
        state.actionMessage = { type: 'success', text: action.payload.message };
        const u = state.users.find(u => u.id === action.payload.userId);
        if (u) { u.estBloqueManuel = true; u.statut = 'Bloque'; }
      })
      .addCase(blockUser.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(unblockUser.pending,   (state) => { state.loading = true; })
      .addCase(unblockUser.fulfilled, (state, action) => {
        state.loading = false;
        state.actionMessage = { type: 'success', text: action.payload.message };
        const u = state.users.find(u => u.id === action.payload.userId);
        if (u) { u.estBloqueManuel = false; u.statut = new Date(u.dateFinAcces) < new Date() ? 'Expire' : 'Actif'; }
      })
      .addCase(unblockUser.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(deleteUser.pending,   (state) => { state.loading = true; })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter(u => u.id !== action.payload.userId);
        state.actionMessage = { type: 'success', text: action.payload.message };
      })
      .addCase(deleteUser.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(renewUser.pending,   (state) => { state.loading = true; })
      .addCase(renewUser.fulfilled, (state, action) => {
        state.loading = false;
        const labels = { mensuel: '1 mois', semestriel: '6 mois', annuel: '1 an' };
        state.actionMessage = { type: 'success', text: `Abonnement renouvelé (${labels[action.payload.duree] || action.payload.duree}) ✓` };
        const u = state.users.find(u => u.id === action.payload.userId);
        if (u) {
          u.dateFinAcces = action.payload.dateFin;
          u.estBloqueManuel = false;
          u.statut = 'Actif';
          u.joursRestants = Math.round((new Date(action.payload.dateFin) - new Date()) / 86400000);
        }
      })
      .addCase(renewUser.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(fetchSuperAdmins.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSuperAdmins.fulfilled, (state, action) => { state.loading = false; state.superAdmins = action.payload; })
      .addCase(fetchSuperAdmins.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(createSuperAdmin.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(createSuperAdmin.fulfilled, (state) => { state.loading = false; state.success = true; state.successMode = 'superAdmin'; })
      .addCase(createSuperAdmin.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(deleteSuperAdmin.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(deleteSuperAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.superAdmins = state.superAdmins.filter(sa => sa.id !== action.payload.id);
        state.actionMessage = { type: 'success', text: action.payload.message };
      })
      .addCase(deleteSuperAdmin.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(fetchAgents.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAgents.fulfilled, (state, action) => { state.loading = false; state.agents = action.payload; })
      .addCase(fetchAgents.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(createAgent.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(createAgent.fulfilled, (state) => { state.loading = false; state.success = true; state.successMode = 'agent'; })
      .addCase(createAgent.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(blockAgent.pending,   (state) => { state.loading = true; })
      .addCase(blockAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.actionMessage = { type: 'success', text: action.payload.message };
        const a = state.agents.find(a => a.id === action.payload.id);
        if (a) a.estBloqueManuel = true;
      })
      .addCase(blockAgent.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(unblockAgent.pending,   (state) => { state.loading = true; })
      .addCase(unblockAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.actionMessage = { type: 'success', text: action.payload.message };
        const a = state.agents.find(a => a.id === action.payload.id);
        if (a) a.estBloqueManuel = false;
      })
      .addCase(unblockAgent.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(deleteAgent.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.agents = state.agents.filter(a => a.id !== action.payload.id);
        state.actionMessage = { type: 'success', text: action.payload.message };
      })
      .addCase(deleteAgent.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(fetchAuditLogs.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAuditLogs.fulfilled, (state, action) => { state.loading = false; state.auditLogs = action.payload; })
      .addCase(fetchAuditLogs.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(changeTenantPack.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(changeTenantPack.fulfilled, (state, action) => {
        state.loading = false;
        state.actionMessage = { type: 'success', text: action.payload.message };
        const t = state.tenants.find(t => t.id === action.payload.id);
        if (t) { t.pack = action.payload.pack; t.limiteVehicules = action.payload.limiteVehicules; t.limiteUtilisateurs = action.payload.limiteUtilisateurs; }
      })
      .addCase(changeTenantPack.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { resetSuccess, clearError, clearActionMessage } = adminSlice.actions;
export default adminSlice.reducer;
